import { forwardRef } from 'react';
import { Field, FieldProps, getIn, useFormikContext } from 'formik';
import { JItem } from '../jformik/jformik.types';
import { useJFormikContext } from '../jformik/jformik.context';
import {
  Tooltip,
  Box,
  Typography,
  Popover,
  TextField,
  Badge,
  FormControlLabel,
  Switch,
  Grid,
  Toolbar,
  Button,
  IconButton,
  InputAdornment,
  FormGroup,
} from '@mui/material';
import moment from 'moment';
import { Close, Event } from '@mui/icons-material';
// import { isSameDay, isWithinInterval, setDate, isAfter } from 'date-fns';
import {
  LocalizationProvider,
  StaticDatePicker,
  StaticDateTimePicker,
  PickersDayProps as MuiPickersDayProps,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import * as React from 'react';
import { PickersDay } from '../../date-ranges-input';
import { RangeItemContainer } from '../../date-ranges-input/RangeItemContainer';
import { useBreakpointQuery, hasValidUSTime } from '../../../utils';

interface DateValue {
  start?: Date;
  end?: Date;
  // date: Date | string | null;
}

export const FDateOrRangePicker = forwardRef((jItem: JItem, ref) => {
  const { name } = jItem;
  const {
    start_name = 'start',
    end_name = 'end',
    date_adornment = 'Due date',
    date_range_adornment = 'Date range',
    start_label = 'START',
    end_label = 'END',
    date_label = 'SELECTED',
  } = jItem.jItemProps || {};
  const smDown = useBreakpointQuery('sm-down');
  const { disabled: jDisabled = false, disableSubmitOnEnter = true } =
    useJFormikContext();
  const disabled =
    jItem.disabled === false ? false : jDisabled || jItem.disabled;
  const { values } = useFormikContext();
  const value = {
    start: getIn(values, start_name),
    end: getIn(values, end_name),
  };
  const [anchorEl, setAnchorEl] =
    React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const [isTime, setIsTime] = React.useState<boolean>(
    !value?.start && hasValidUSTime(value?.end)
  );
  const getLabel = ({ start, end }: DateValue) => {
    // const numDays = moment(end).diff(moment.now(), 'days');
    return {
      label: !!start
        ? `${start ? moment(start).format('MM/DD') : ''} - ${
            end ? moment(end).format('MM/DD') : ''
          }`
        : !!end
        ? isTime
          ? `${moment(end).format('MM/DD h:mm A')}`
          : `${moment(end).format('MM/DD')}`
        : undefined,
      adornment: getInputAdornmentText({ start, end }),
    };
  };

  const getInputAdornmentText = ({ start, end }: DateValue) => {
    // const numDays = moment(end).diff(moment.now(), 'days');
    return !!start
      ? date_range_adornment
      : !!end
      ? date_adornment
      : `Select date(s)`;
  };

  const [dateValue, setDateValue] = React.useState<DateValue>(
    value.start || value.end
      ? value
      : jItem.defaultValue || {
          start: null,
          end: null,
        }
  );
  const [label, setLabel] = React.useState<
    | {
        label: string | undefined;
        adornment: string | undefined;
      }
    | undefined
  >(
    getLabel(
      value.start || value.end
        ? value
        : jItem.defaultValue || {
            start: null,
            end: null,
          }
    )
  );
  const [isRange, setIsRange] = React.useState<boolean>(
    !!value?.start || jItem.jItemProps?.rangeOnly
  );
  const { start, end } = dateValue;
  const handleChange = (newValue: any) => {
    if (newValue) {
      if (isRange) {
        if (start && !end && newValue > start) {
          setDateValue(prev => ({
            ...prev,
            end: newValue,
          }));
        } else {
          setDateValue(prev => {
            if (prev.start && isSameDay(prev.start, newValue)) {
              return { start: undefined, end: undefined };
            } else {
              return { start: newValue, end: undefined };
            }
          });
        }
      } else {
        setDateValue(prev => {
          if (prev.end && isSameDay(prev.end, newValue) && !isTime) {
            return { start: undefined, end: undefined };
          } else {
            return {
              start: undefined,
              end: newValue,
            };
          }
        });
      }
    }
  };

  // React.useEffect(() => {
  //   setDateValue(value);
  //   setLabel(getLabel(value));
  // }, [value]);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const isSameDay = (a: Date, b: Date) => {
    return moment(a).isSame(b, 'day');
  };
  const isWithinInterval = (
    date: Date,
    { start, end }: { start: Date; end: Date }
  ) => {
    return moment(date).isBetween(start, end, 'day');
  };
  const isAfter = (a: Date, b: Date) => {
    return moment(a).isAfter(b, 'day');
  };

  const renderDay = (
    date: Date,
    selectedDates: Array<Date | null>,
    pickersDayProps: MuiPickersDayProps<Date>
  ) => {
    const dayIsBetween =
      start && end && isAfter(end, start)
        ? isWithinInterval(date, { start, end })
        : false;
    const isFirstDay = start && isSameDay(date, start) ? true : false;
    const isLastDay = end && isSameDay(date, end) ? true : false;

    return (
      <Badge
        key={pickersDayProps?.day?.toString()}
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          isFirstDay && isRange ? (
            <Typography
              variant="inherit"
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                color: theme => theme.palette.secondary.main,
                backgroundColor: 'white',
                border: theme => `1px solid ${theme.palette.secondary.main}`,
                px: '2px',
                py: '1px',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
              }}
            >
              {start_label}
            </Typography>
          ) : isLastDay && isRange ? (
            <Typography
              variant="inherit"
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                color: theme => theme.palette.secondary.main,
                backgroundColor: 'white',
                border: theme => `1px solid ${theme.palette.secondary.main}`,
                px: '2px',
                py: '1px',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
              }}
            >
              {end_label}
            </Typography>
          ) : pickersDayProps?.today ? (
            <Typography
              variant="inherit"
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                color: theme => theme.palette.secondary.main,
                backgroundColor: 'white',
                border: theme => `1px solid ${theme.palette.secondary.main}`,
                px: '2px',
                py: '1px',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
              }}
            >
              TODAY
            </Typography>
          ) : pickersDayProps?.selected || isLastDay ? (
            <Typography
              variant="inherit"
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                color: theme => theme.palette.secondary.main,
                backgroundColor: 'white',
                border: theme => `1px solid ${theme.palette.secondary.main}`,
                px: '2px',
                py: '1px',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
              }}
            >
              {date_label}
            </Typography>
          ) : undefined
        }
      >
        <PickersDay
          {...pickersDayProps}
          selected={pickersDayProps?.selected || isFirstDay || isLastDay}
          onDaySelect={(day: Date, isFinish: any) => {
            if (end && isSameDay(end, day)) {
              setDateValue({ start: undefined, end: undefined });
            } else {
              pickersDayProps.onDaySelect(day, isFinish);
            }
          }}
          dayIsBetween={dayIsBetween}
          isFirstDay={isFirstDay}
          isLastDay={isLastDay}
        />
      </Badge>
    );
  };
  return (
    <Field name={name} ref={ref}>
      {({
        field: { name, value },
        form: { touched, errors, submitCount, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
        meta,
      }: FieldProps) => {
        const drError =
          typeof getIn(errors, name) === 'string'
            ? getIn(errors, name)
            : undefined;
        return (
          <div>
            <Box
              onClick={(event: any) => {
                event.preventDefault();
                event.stopPropagation();
                if (!disabled) {
                  handleClick(event);
                }
              }}
            >
              <TextField
                id={`${jItem.name}-field`}
                onKeyPress={e => {
                  disableSubmitOnEnter &&
                    e.key === 'Enter' &&
                    e.preventDefault();
                }}
                disabled={disabled}
                label={
                  jItem.jItemProps?.labelFunc
                    ? jItem?.jItemProps?.labelFunc(name, values, {
                        setFieldValue,
                      })
                    : jItem.label
                }
                name={name}
                value={label?.label || ''}
                variant="outlined"
                // size={smDown ? 'small' : 'medium'}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={'Select Date(s)'}>
                        <IconButton
                          disabled={jItem.disabled}
                          onClick={(event: any) => {
                            event.preventDefault();
                            event.stopPropagation();
                            if (!disabled) {
                              handleClick(event);
                            }
                          }}
                        >
                          <Event color="secondary" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                  startAdornment: (
                    <InputAdornment position="start">
                      {label?.adornment}
                    </InputAdornment>
                  ),
                }}
                error={
                  !!(
                    drError &&
                    (getIn(touched, name) ||
                      submitCount ||
                      jItem?.jItemProps?.touched)
                  )
                }
                helperText={
                  getIn(touched, name) ||
                  submitCount ||
                  jItem?.jItemProps?.touched
                    ? drError
                    : null
                }
              />
            </Box>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={(event: any) => {
                event.preventDefault();
                event.stopPropagation();
                handleClose();
              }}
              onClick={(event: any) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                {!isTime ? (
                  <StaticDatePicker
                    showToolbar={true}
                    components={{
                      ActionBar: (props: any) => (
                        <Grid
                          container
                          spacing={0}
                          direction="row"
                          justifyContent="flex-end"
                          alignItems="center"
                          flexWrap={'nowrap'}
                          sx={{ mb: 2 }}
                        >
                          <Grid item>
                            <Button onClick={() => handleClose()}>
                              Cancel
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              onClick={() => {
                                setFieldValue(
                                  start_name,
                                  dateValue?.start || null
                                );
                                setFieldValue(end_name, dateValue?.end || null);
                                jItem.jItemProps?.onAccept &&
                                  jItem.jItemProps?.onAccept(
                                    dateValue,
                                    values,
                                    {
                                      setFieldValue,
                                    }
                                  );
                                setLabel(getLabel(dateValue));
                                handleClose();
                              }}
                            >
                              Ok
                            </Button>
                          </Grid>
                        </Grid>
                      ),
                    }}
                    toolbarTitle={
                      <Grid
                        container
                        spacing={0}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap={'nowrap'}
                        sx={{ width: '100%' }}
                      >
                        <Grid item sx={{ mr: isRange ? 2 : end ? 3 : 8 }}>
                          {isRange ? 'Select Date Range' : 'Select Date'}
                        </Grid>
                        <Grid item>
                          {jItem.jItemProps?.rangeOnly === undefined && (
                            <FormGroup>
                              {(isRange || !dateValue.end) && (
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isRange}
                                      onClick={() => {
                                        setDateValue({
                                          start: undefined,
                                          end: undefined,
                                        });
                                        setIsRange(pv => !pv);
                                      }}
                                      size="small"
                                      color="secondary"
                                    />
                                  }
                                  label={
                                    <Typography variant="caption">
                                      ADD DATE RANGE
                                    </Typography>
                                  }
                                />
                              )}
                              {!isRange && dateValue.end && (
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isTime}
                                      onClick={() => {
                                        setDateValue(pv => ({
                                          start: undefined,
                                          end: moment(pv.end)
                                            .startOf('day')
                                            .toDate(),
                                        }));
                                        setIsTime(pv => !pv);
                                      }}
                                      size="small"
                                      color="secondary"
                                    />
                                  }
                                  label={
                                    <Typography variant="caption">
                                      ADD TIME
                                    </Typography>
                                  }
                                />
                              )}
                            </FormGroup>
                          )}
                        </Grid>
                      </Grid>
                    }
                    value={isRange ? dateValue || null : dateValue?.end || null}
                    onChange={value => handleChange(value)}
                    onAccept={(newValue: any) => {
                      setFieldValue(start_name, newValue?.start || null);
                      setFieldValue(end_name, newValue?.end || null);
                      jItem.jItemProps?.onAccept &&
                        jItem.jItemProps?.onAccept(newValue, values, {
                          setFieldValue,
                        });
                      setLabel(getLabel(newValue));
                      handleClose();
                    }}
                    renderInput={params => {
                      return <TextField {...params} />;
                    }}
                    renderDay={renderDay}
                    {...(isRange
                      ? {
                          ToolbarComponent: () => {
                            return (
                              <Toolbar
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  padding: '16px 24px',
                                }}
                              >
                                <Grid
                                  container
                                  spacing={0}
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  flexWrap={'nowrap'}
                                  sx={{ width: '100%' }}
                                >
                                  <Grid item sx={{ mr: isRange ? 2 : 8 }}>
                                    <Typography
                                      variant="overline"
                                      sx={{
                                        color: theme =>
                                          theme.palette.text.secondary,
                                      }}
                                    >
                                      {isRange
                                        ? 'Select Date Range'
                                        : 'Select Date'}
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    {jItem.jItemProps?.rangeOnly ===
                                      undefined && (
                                      <FormGroup>
                                        {(isRange || !dateValue.end) && (
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                checked={isRange}
                                                onClick={() => {
                                                  setDateValue({
                                                    start: undefined,
                                                    end: undefined,
                                                  });
                                                  setIsRange(pv => !pv);
                                                }}
                                                size="small"
                                                color="secondary"
                                              />
                                            }
                                            label={
                                              <Typography variant="caption">
                                                ADD DATE RANGE
                                              </Typography>
                                            }
                                          />
                                        )}
                                        {!isRange && dateValue.end && (
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                checked={isTime}
                                                onClick={() => {
                                                  setDateValue(pv => ({
                                                    start: undefined,
                                                    end: moment(pv.end)
                                                      .startOf('day')
                                                      .toDate(),
                                                  }));
                                                  setIsTime(pv => !pv);
                                                }}
                                                size="small"
                                                color="secondary"
                                              />
                                            }
                                            label={
                                              <Typography variant="caption">
                                                ADD TIME
                                              </Typography>
                                            }
                                          />
                                        )}
                                      </FormGroup>
                                    )}
                                  </Grid>
                                </Grid>
                                <Box sx={{ width: '100%' }}>
                                  <RangeItemContainer
                                    action={
                                      <IconButton
                                        color="inherit"
                                        size="small"
                                        onClick={e => {
                                          e.stopPropagation();
                                          setDateValue({
                                            start: undefined,
                                            end: undefined,
                                          });
                                        }}
                                      >
                                        <Close sx={{ fontSize: 'inherit' }} />
                                      </IconButton>
                                    }
                                  >
                                    {`${
                                      dateValue.start
                                        ? moment(dateValue.start).format(
                                            'MM/DD'
                                          )
                                        : ''
                                    } - ${
                                      dateValue.end
                                        ? moment(dateValue.end).format('MM/DD')
                                        : ''
                                    }`}
                                  </RangeItemContainer>
                                </Box>
                              </Toolbar>
                            );
                          },
                        }
                      : {})}
                  />
                ) : (
                  <StaticDateTimePicker
                    showToolbar={true}
                    components={{
                      ActionBar: (props: any) => (
                        <Grid
                          container
                          spacing={0}
                          direction="row"
                          justifyContent="flex-end"
                          alignItems="center"
                          flexWrap={'nowrap'}
                          sx={{ mb: 2 }}
                        >
                          <Grid item>
                            <Button onClick={() => handleClose()}>
                              Cancel
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              onClick={() => {
                                setFieldValue(
                                  start_name,
                                  dateValue?.start || null
                                );
                                setFieldValue(end_name, dateValue?.end || null);
                                jItem.jItemProps?.onAccept &&
                                  jItem.jItemProps?.onAccept(
                                    dateValue,
                                    values,
                                    {
                                      setFieldValue,
                                    }
                                  );
                                setLabel(getLabel(dateValue));
                                handleClose();
                              }}
                            >
                              Ok
                            </Button>
                          </Grid>
                        </Grid>
                      ),
                    }}
                    toolbarTitle={
                      <Grid
                        container
                        spacing={0}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap={'nowrap'}
                        sx={{ width: '100%' }}
                      >
                        <Grid item sx={{ mr: isRange ? 2 : end ? 3 : 8 }}>
                          {isRange ? 'Select Date Range' : 'Select Date'}
                        </Grid>
                        <Grid item>
                          {jItem.jItemProps?.rangeOnly === undefined && (
                            <FormGroup>
                              {(isRange || !dateValue.end) && (
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isRange}
                                      onClick={() => {
                                        setDateValue({
                                          start: undefined,
                                          end: undefined,
                                        });
                                        setIsRange(pv => !pv);
                                      }}
                                      size="small"
                                      color="secondary"
                                    />
                                  }
                                  label={
                                    <Typography variant="caption">
                                      ADD DATE RANGE
                                    </Typography>
                                  }
                                />
                              )}
                              {!isRange && dateValue.end && (
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isTime}
                                      onClick={() => {
                                        setDateValue(pv => ({
                                          start: undefined,
                                          end: moment(pv.end)
                                            .startOf('day')
                                            .toDate(),
                                        }));
                                        setIsTime(pv => !pv);
                                      }}
                                      size="small"
                                      color="secondary"
                                    />
                                  }
                                  label={
                                    <Typography variant="caption">
                                      ADD TIME
                                    </Typography>
                                  }
                                />
                              )}
                            </FormGroup>
                          )}
                        </Grid>
                      </Grid>
                    }
                    value={isRange ? dateValue || null : dateValue?.end || null}
                    onChange={value => handleChange(value)}
                    onAccept={(newValue: any) => {
                      setFieldValue(start_name, newValue?.start || null);
                      setFieldValue(end_name, newValue?.end || null);
                      jItem.jItemProps?.onAccept &&
                        jItem.jItemProps?.onAccept(newValue, values, {
                          setFieldValue,
                        });
                      setLabel(getLabel(newValue));
                      handleClose();
                    }}
                    renderInput={params => {
                      return <TextField {...params} />;
                    }}
                    renderDay={renderDay}
                    {...(isRange
                      ? {
                          ToolbarComponent: () => {
                            return (
                              <Toolbar
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  padding: '16px 24px',
                                }}
                              >
                                <Grid
                                  container
                                  spacing={0}
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  flexWrap={'nowrap'}
                                  sx={{ width: '100%' }}
                                >
                                  <Grid item sx={{ mr: isRange ? 2 : 8 }}>
                                    <Typography
                                      variant="overline"
                                      sx={{
                                        color: theme =>
                                          theme.palette.text.secondary,
                                      }}
                                    >
                                      {isRange
                                        ? 'Select Date Range'
                                        : 'Select Date'}
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    {jItem.jItemProps?.rangeOnly ===
                                      undefined && (
                                      <FormGroup>
                                        {(isRange || !dateValue.end) && (
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                checked={isRange}
                                                onClick={() => {
                                                  setDateValue({
                                                    start: undefined,
                                                    end: undefined,
                                                  });
                                                  setIsRange(pv => !pv);
                                                }}
                                                size="small"
                                                color="secondary"
                                              />
                                            }
                                            label={
                                              <Typography variant="caption">
                                                ADD DATE RANGE
                                              </Typography>
                                            }
                                          />
                                        )}
                                        {!isRange && dateValue.end && (
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                checked={isTime}
                                                onClick={() => {
                                                  setDateValue(pv => ({
                                                    start: undefined,
                                                    end: moment(pv.end)
                                                      .startOf('day')
                                                      .toDate(),
                                                  }));
                                                  setIsTime(pv => !pv);
                                                }}
                                                size="small"
                                                color="secondary"
                                              />
                                            }
                                            label={
                                              <Typography variant="caption">
                                                ADD TIME
                                              </Typography>
                                            }
                                          />
                                        )}
                                      </FormGroup>
                                    )}
                                  </Grid>
                                </Grid>
                                <Box sx={{ width: '100%' }}>
                                  <RangeItemContainer
                                    action={
                                      <IconButton
                                        color="inherit"
                                        size="small"
                                        onClick={e => {
                                          e.stopPropagation();
                                          setDateValue({
                                            start: undefined,
                                            end: undefined,
                                          });
                                        }}
                                      >
                                        <Close sx={{ fontSize: 'inherit' }} />
                                      </IconButton>
                                    }
                                  >
                                    {`${
                                      dateValue.start
                                        ? moment(dateValue.start).format(
                                            'MM/DD'
                                          )
                                        : ''
                                    } - ${
                                      dateValue.end
                                        ? moment(dateValue.end).format('MM/DD')
                                        : ''
                                    }`}
                                  </RangeItemContainer>
                                </Box>
                              </Toolbar>
                            );
                          },
                        }
                      : {})}
                  />
                )}
              </LocalizationProvider>
            </Popover>
          </div>
        );
      }}
    </Field>
  );
});
